// components/NotificationBell/NotificationDropdown.tsx
import React, { useState } from 'react';
import { Tabs, List, Empty, Spin, Button, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { UserInfoNotification } from '../../types/notifications';
import { NotificationItem } from './NotificationItem';
import { useTheme } from '../../contexts/ThemeContext';

const { Text } = Typography;

interface NotificationDropdownProps {
  notifications: UserInfoNotification[];
  loading: boolean;
  onResolve: (id: string) => void;
  onRefresh: () => void;
  resolvingId?: string | null;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  loading,
  onResolve,
  onRefresh,
  resolvingId
}) => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState<string>('all');

  // Filtrar notificaciones según la pestaña activa
  const filteredNotifications = React.useMemo(() => {
    if (activeTab === 'pending') {
      return notifications.filter(n => n.status === 'PENDING');
    }
    if (activeTab === 'resolved') {
      return notifications.filter(n => n.status === 'RESOLVED');
    }
    return notifications;
  }, [notifications, activeTab]);

  const pendingCount = notifications.filter(n => n.status === 'PENDING').length;
  const resolvedCount = notifications.filter(n => n.status === 'RESOLVED').length;

  const tabItems = [
    {
      key: 'all',
      label: `Todas (${notifications.length})`,
      children: null
    },
    {
      key: 'pending',
      label: `Pendientes (${pendingCount})`,
      children: null
    },
    {
      key: 'resolved',
      label: `Resueltas (${resolvedCount})`,
      children: null
    }
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div
        style={{
          padding: '16px',
          borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#f0f0f0'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: theme === 'dark' ? '#141414' : '#fff',
        }}
      >
        <Text strong style={{ fontSize: 16 }}>
          Notificaciones
        </Text>
        <Button
          type="text"
          size="small"
          icon={<ReloadOutlined />}
          onClick={onRefresh}
          loading={loading}
        >
          Actualizar
        </Button>
      </div>

      {/* Tabs */}
      <div style={{ backgroundColor: theme === 'dark' ? '#141414' : '#fff' }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          style={{ padding: '0 16px' }}
        />
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          backgroundColor: theme === 'dark' ? '#141414' : '#fff',
        }}
      >
        {loading && notifications.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spin tip="Cargando notificaciones..." />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <Empty
            description={
              activeTab === 'pending'
                ? 'No hay notificaciones pendientes'
                : activeTab === 'resolved'
                ? 'No hay notificaciones resueltas'
                : 'No hay notificaciones'
            }
            style={{ padding: 40 }}
          />
        ) : (
          <List
            dataSource={filteredNotifications}
            renderItem={(notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onResolve={onResolve}
                loading={resolvingId === notification.id}
              />
            )}
          />
        )}
      </div>
    </div>
  );
};
